import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"
import { ImageFit, VirtualBackgroundProcessor } from "@twilio/video-processors"
import {
  connect,
  ConnectOptions,
  createLocalTracks,
  LocalDataTrack,
  LocalTrack,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
  TwilioError,
} from "twilio-video"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Switch } from "@/components/ui/switch"
import { useIsFeatureEnabled } from "@/hooks/useIsFeatureEnabled"
import { getInsensitiveSearchParams } from "@/lib/get-insensitive-search-params"
import { TranscriptDisplay } from "@/modules/speech-to-text/components/TranscriptDisplay"
import { AUDIO_SAMPLE_RATE, useSpeechToText } from "@/modules/speech-to-text/hooks/useSpeechToText"
import { TranscriptSpeaker } from "@/modules/speech-to-text/types"
import { DummyRoomInformation } from "@/modules/video-call/components/dummy-call/dummy-room-information"
import { VideoCall } from "@/modules/video-call/components/video-call"
import { getBackgroundImage } from "@/modules/video-call/helpers/background.helper"
import { isPermissionDenied, permissionDeniedToast } from "@/modules/video-call/helpers/device.helper"
import { useVideoCallAppointmentParam } from "@/modules/video-call/hooks/useVideoCallAppointmentParam"
import { CreateOrAppendCallTranscriptInput } from "@/modules/video-call/services/__generated__/useCreateOrAppendCallTranscriptMutation.graphql"
import { StartVideoCallInput } from "@/modules/video-call/services/__generated__/useCreateVideoCallMutation.graphql"
import { DummyAcceptVideoCallInput } from "@/modules/video-call/services/__generated__/useDummyAcceptVideoCallMutation.graphql"
import { useCreateOrAppendCallTranscriptMutation } from "@/modules/video-call/services/useCreateOrAppendCallTranscriptMutation"
import { VideoCallRoom } from "@/modules/video-call/services/useCreateVideoCallMutation"
import { useDummyAcceptVideoCallMutation } from "@/modules/video-call/services/useDummyAcceptVideoCall"
import { useDummyCreateVideoCallMutation } from "@/modules/video-call/services/useDummyCreateVideoCallMutation"
import { useEndVideoCallMutation } from "@/modules/video-call/services/useEndVideoCallMutation"
import {
  BLUR_ASSETS_PATH,
  DEFAULT_AUDIO_CONSTRAINTS,
  DEFAULT_VIDEO_CONSTRAINTS,
} from "@/modules/video-call/types/video-call-configuration"
import { SofiaEnvironment } from "@/sofia-environment"
import useTranscriptStore from "@/stores/useTranscriptStore"
import useVideoCallStore from "@/stores/useVideoCallStore"
import { Language } from "@/types/__generated__/union-types"

const DummyVideoCallLayoutContent = () => {
  const { t } = useTranslation()
  const isSpeechToTextEnabled = useIsFeatureEnabled("SPEECH_TO_TEXT")
  // This callMutation prevent trigger mutation multiple time when video call popup was opened
  const callMutation = useRef(false)
  const transcriptStartedAt = useRef<Date | null>(null)
  const lastSavedTranscriptIndex = useRef(0)
  const transcriptIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const callTranscriptId = useRef<string | null>(null)
  const appointmentId = useVideoCallAppointmentParam()
  const [searchParams] = useSearchParams()

  // Read progressNoteId and language from URL params (for dummy call)
  const progressNoteId = getInsensitiveSearchParams(searchParams, "progressNoteId").value
  const appointmentLanguage: Language =
    (getInsensitiveSearchParams(searchParams, "language").value as Language | null) ?? "ENGLISH"

  const [room, setRoom] = useState<VideoCallRoom | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDisconnected, setIsDisconnected] = useState<boolean>(false)
  const [showTranscript, setShowTranscript] = useState<boolean>(false)
  const [isPatientVoiceEnabled, setIsPatientVoiceEnabled] = useState<boolean>(false)
  const remoteAudioTrackRef = useRef<RemoteAudioTrack | null>(null)
  const prevIsPatientVoiceEnabledRef = useRef<boolean>(false)
  const [dummyAcceptVideoCall] = useDummyAcceptVideoCallMutation()
  const [dummyCreateVideoCall] = useDummyCreateVideoCallMutation()
  const [createOrAppendCallTranscript] = useCreateOrAppendCallTranscriptMutation()
  const endVideoCall = useEndVideoCallMutation()
  const setLocalTracks = useVideoCallStore((state) => state.setLocalTracks)
  const setParticipants = useVideoCallStore((state) => state.setParticipants)

  // Determine speaker roles based on whether appointmentId exists
  // If appointmentId exists -> user is the doctor (agent), remote is patient
  // If appointmentId doesn't exist -> user is the patient, remote is agent (doctor)
  const { localTranscriptSpeaker, remoteTranscriptSpeaker } = useMemo<{
    localTranscriptSpeaker: TranscriptSpeaker
    remoteTranscriptSpeaker: TranscriptSpeaker
  }>(() => {
    if (appointmentId) {
      return { localTranscriptSpeaker: "AGENT", remoteTranscriptSpeaker: "PATIENT" }
    }
    return { localTranscriptSpeaker: "PATIENT", remoteTranscriptSpeaker: "AGENT" }
  }, [appointmentId])

  // Speech-to-text for local user - captures microphone
  const speechToTextLocal = useSpeechToText({
    language: appointmentLanguage,
    audioSource: "microphone",
    speaker: localTranscriptSpeaker,
    enabled: isSpeechToTextEnabled,
    onError: (error) => {
      console.error("Speech-to-text local error:", error)
    },
  })

  // Speech-to-text for remote user - uses remote audio track from Twilio
  // Start/stop is controlled manually via the useEffect below based on isPatientVoiceEnabled
  const speechToTextRemote = useSpeechToText({
    language: appointmentLanguage,
    audioSource: "remoteTrack",
    speaker: remoteTranscriptSpeaker,
    enabled: isSpeechToTextEnabled,
    onError: (error) => {
      console.error("Speech-to-text remote error:", error)
    },
  })

  const transcriptStore = useTranscriptStore()
  console.log("transcriptStore:", transcriptStore)

  const handleMicrophoneToggle = useCallback(
    (isMuted: boolean) => {
      if (isMuted) {
        console.log("[DummyVideoCall] Microphone muted - stopping speech-to-text")
        speechToTextLocal.stopRecording()
      } else {
        console.log("[DummyVideoCall] Microphone unmuted - resuming speech-to-text")
        speechToTextLocal.startRecording().catch((error) => {
          console.error("Failed to resume local speech-to-text:", error)
        })
      }
    },
    [speechToTextLocal],
  )

  // Save transcripts using the createCallTranscript mutation
  const saveTranscripts = useCallback(
    (roomId: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const allTranscripts = transcriptStore.getMergedTranscripts()
        const newTranscripts = allTranscripts.slice(lastSavedTranscriptIndex.current)

        console.log("=== SAVING TRANSCRIPTS ===")
        console.log("Total transcripts:", allTranscripts.length)
        console.log("New transcripts since last save:", newTranscripts.length)

        // Only save if we have new transcripts and required IDs
        if (newTranscripts.length === 0) {
          console.log("No new transcripts to save")
          resolve()
          return
        }

        if (!appointmentId) {
          console.warn("Cannot save transcripts: appointmentId is not available")
          resolve()
          return
        }

        if (!progressNoteId) {
          console.warn("Cannot save transcripts: progressNoteId is not available")
          resolve()
          return
        }

        // Log new transcripts for debugging
        newTranscripts.forEach((t, index) => {
          console.log(
            `[${lastSavedTranscriptIndex.current + index + 1}] ${t.speaker.toUpperCase()} (${new Date(t.timestamp).toLocaleTimeString()}): ${t.text}`,
          )
        })

        const input: CreateOrAppendCallTranscriptInput = {
          id: callTranscriptId.current,
          roomId,
          appointmentId,
          progressNoteId,
          transcript: newTranscripts.map((t) => ({
            id: t.id,
            text: t.text,
            timestamp: t.timestamp,
            isFinal: t.isFinal,
            speaker: t.speaker,
          })),
          transcriptStartedAt: transcriptStartedAt.current?.toISOString() ?? new Date().toISOString(),
        }

        createOrAppendCallTranscript({
          variables: { input },
          onCompleted: (response) => {
            const savedId = response.createOrAppendCallTranscript?.callTranscript?.id ?? null
            console.log("Transcript saved successfully:", savedId)
            callTranscriptId.current = savedId
            lastSavedTranscriptIndex.current = allTranscripts.length
            resolve()
          },
          onError: (error) => {
            console.error("Failed to save transcript:", error)
            reject(error)
          },
        })

        console.log("=== END TRANSCRIPTS ===")
      })
    },
    [transcriptStore, appointmentId, progressNoteId, createOrAppendCallTranscript],
  )

  const removeTrack = useCallback((tracks: LocalTrack[]) => {
    tracks.forEach((track) => {
      if (track && (track.kind === "audio" || track.kind === "video")) {
        track.stop()
      }
    })
  }, [])

  const removeAllListeners = useCallback((tracks: Map<string, RemoteTrackPublication>) => {
    tracks.forEach((track) => track.removeAllListeners())
  }, [])

  const initializeBlurProcessor = useCallback(async (tracks: LocalTrack[]) => {
    const processor = new VirtualBackgroundProcessor({
      assetsPath: BLUR_ASSETS_PATH,
      backgroundImage: await getBackgroundImage(),
      fitType: ImageFit.Cover,
    })
    await processor.loadModel()

    tracks.forEach((track) => {
      if (track && track.kind === "video") {
        track.addProcessor(processor, {
          inputFrameBufferType: "videoframe",
          outputFrameBufferContextType: "bitmaprenderer",
        })
      }
    })
    return tracks
  }, [])

  const initializeLocalTracks = useCallback(async () => {
    const isCameraPermissionDenied = await isPermissionDenied("camera")
    const isMicrophonePermissionDenied = await isPermissionDenied("microphone")

    permissionDeniedToast(isCameraPermissionDenied, isMicrophonePermissionDenied)

    const tracks = await createLocalTracks({
      audio: !isCameraPermissionDenied && DEFAULT_AUDIO_CONSTRAINTS,
      video: !isMicrophonePermissionDenied && DEFAULT_VIDEO_CONSTRAINTS,
    })
    const tracksWithProcessor = await initializeBlurProcessor(tracks)
    setLocalTracks(tracksWithProcessor)
    return tracks
  }, [initializeBlurProcessor, setLocalTracks])

  // Store remote audio track when subscribed (for mute/unmute and speech-to-text control)
  const handleTrackSubscribed = useCallback((track: RemoteTrack) => {
    console.log("[DummyVideoCall] handleTrackSubscribed called, track kind:", track.kind)
    if (track.kind === "audio") {
      const audioTrack = track as RemoteAudioTrack
      // Store the audio track reference for mute/unmute control
      remoteAudioTrackRef.current = audioTrack

      // Initially mute the track - user must explicitly enable patient voice
      audioTrack.mediaStreamTrack.enabled = false
      console.log("[DummyVideoCall] Remote audio track subscribed and muted (patient voice disabled by default)")
    }
  }, [])

  const subscribeRoomEvent = useCallback(
    (connectedRoom: Room, localTracks: LocalTrack[], roomSid: string) => {
      const { participants } = connectedRoom
      const roomDisconnected = (room: Room, error: TwilioError) => {
        if (error) {
          console.log("Disconnected:", error)
        }

        removeTrack(localTracks)
        speechToTextLocal.stopRecording()
        speechToTextRemote.stopRecording()
        setIsDisconnected(true)
        console.log("call close", room)
      }

      const participantConnected = (participant: RemoteParticipant) => {
        console.log("[DummyVideoCall] participantConnected:", participant.identity)
        setParticipants([participant])

        // Subscribe to existing tracks
        console.log("[DummyVideoCall] Checking existing tracks, count:", participant.tracks.size)
        participant.tracks.forEach((publication) => {
          console.log(
            "[DummyVideoCall] Existing track publication:",
            publication.kind,
            "isSubscribed:",
            publication.isSubscribed,
          )
          if (publication.isSubscribed && publication.track) {
            handleTrackSubscribed(publication.track)
          }
        })

        // Listen for new track subscriptions
        console.log("[DummyVideoCall] Adding trackSubscribed listener")
        participant.on("trackSubscribed", handleTrackSubscribed)
      }

      const participantDisconnected = (participant: RemoteParticipant) => {
        // Remove all tracks listener from participant who left the room
        removeAllListeners(participant.tracks)
        participant.off("trackSubscribed", handleTrackSubscribed)
        speechToTextRemote.stopRecording()
        setParticipants([])
      }

      const disconnectRoom = () => {
        if (transcriptIntervalRef.current) {
          clearInterval(transcriptIntervalRef.current)
          transcriptIntervalRef.current = null
        }
        speechToTextLocal.stopRecording()
        speechToTextRemote.stopRecording()
        saveTranscripts(roomSid)
        connectedRoom.disconnect()
      }

      // Handle existing participants (patient may already be in the room)
      console.log("[DummyVideoCall] Checking existing participants, count:", participants.size)
      participants.forEach((participant) => {
        console.log("[DummyVideoCall] Existing participant:", participant.identity)
        participant.tracks.forEach((publication) => {
          console.log(
            "[DummyVideoCall] Existing participant track:",
            publication.kind,
            "isSubscribed:",
            publication.isSubscribed,
          )
          if (publication.isSubscribed && publication.track) {
            handleTrackSubscribed(publication.track)
          }
        })
        participant.on("trackSubscribed", handleTrackSubscribed)
      })

      setParticipants(Array.from(participants.values()))

      connectedRoom.on("disconnected", roomDisconnected)
      connectedRoom.on("participantConnected", participantConnected)
      connectedRoom.on("participantDisconnected", participantDisconnected)

      // This event trigger disconnect room event when participant close browser
      window.addEventListener("beforeunload", disconnectRoom)
      return () => {
        window.removeEventListener("beforeunload", disconnectRoom)
        connectedRoom.off("disconnected", roomDisconnected)
        connectedRoom.off("participantConnected", participantConnected)
        connectedRoom.off("participantDisconnected", participantDisconnected)
        participants.forEach((participant) => {
          participant.off("trackSubscribed", handleTrackSubscribed)
        })
      }
    },
    [
      removeAllListeners,
      removeTrack,
      setParticipants,
      speechToTextLocal,
      speechToTextRemote,
      handleTrackSubscribed,
      saveTranscripts,
    ],
  )

  const connectToRoom = useCallback(
    async (room: VideoCallRoom) => {
      const localTracks = await initializeLocalTracks()
      if (!room?.roomSid || !localTracks) {
        return
      }
      const localDataTrack = new LocalDataTrack()
      const connectOptions: ConnectOptions = {
        maxAudioBitrate: AUDIO_SAMPLE_RATE,
        tracks: [...localTracks, localDataTrack],
        insights: false,
        region: "de1",
      }
      const connectedRoom = await connect(room.accessToken, connectOptions)

      const dummyAcceptVideoCallInput: DummyAcceptVideoCallInput = {
        roomSid: room.roomSid,
      }
      dummyAcceptVideoCall({ variables: { input: dummyAcceptVideoCallInput } })

      setIsLoading(false)

      // Start speech-to-text for local user (microphone) when call connects
      console.log("[DummyVideoCall] Starting local speech-to-text")
      transcriptStartedAt.current = new Date()
      lastSavedTranscriptIndex.current = 0
      callTranscriptId.current = null
      speechToTextLocal.startRecording().catch((error) => {
        console.error("Failed to start local speech-to-text:", error)
      })

      // Set up interval to save transcripts (only if feature enabled)
      if (isSpeechToTextEnabled) {
        transcriptIntervalRef.current = setInterval(() => {
          saveTranscripts(room.roomSid)
        }, SofiaEnvironment.SpeechToText.TranscriptSaveIntervalMs)
      }

      // Remote speech-to-text starts automatically when remote audio track is subscribed
      // (handled in handleTrackSubscribed callback)
      console.log("[DummyVideoCall] Room connection complete, waiting for remote participant to join")

      subscribeRoomEvent(connectedRoom, localTracks, room.roomSid)
    },
    [
      initializeLocalTracks,
      dummyAcceptVideoCall,
      subscribeRoomEvent,
      speechToTextLocal,
      saveTranscripts,
      isSpeechToTextEnabled,
    ],
  )

  const startVideoCall = useCallback(
    (createVideoCallInput: StartVideoCallInput) => {
      dummyCreateVideoCall({
        variables: {
          input: createVideoCallInput,
        },
        onCompleted: (response) => {
          const errMessage = response.dummyStartVideoCall?.errors?.[0]?.message
          if (errMessage) {
            setIsLoading(false)
            console.log(errMessage)
            return
          }
          const output = response.dummyStartVideoCall.dummyStartVideoCallOutput
          const roomInformation: VideoCallRoom = {
            roomSid: output?.roomSid ?? "",
            accessToken: output?.sofiaUserToken ?? "",
            dummyToken: output?.patientToken,
          }
          setRoom(roomInformation)
          connectToRoom(roomInformation)
        },
      })
    },
    [connectToRoom, dummyCreateVideoCall],
  )

  const dummyStartVideoCall = useCallback(() => {
    const roomInformation: VideoCallRoom = {
      roomSid: getInsensitiveSearchParams(searchParams, "sid").value ?? "",
      accessToken: getInsensitiveSearchParams(searchParams, "token").value ?? "",
    }
    setRoom(roomInformation)
    connectToRoom(roomInformation)
  }, [connectToRoom, searchParams])

  const handleCloseVideoCall = async () => {
    setIsLoading(true)
    if (transcriptIntervalRef.current) {
      clearInterval(transcriptIntervalRef.current)
      transcriptIntervalRef.current = null
    }
    speechToTextLocal.stopRecording()
    speechToTextRemote.stopRecording()
    if (room) {
      const endVideoCallPromise = new Promise<void>((resolve) => {
        endVideoCall({
          variables: { input: { roomSid: room.roomSid } },
          onCompleted: () => resolve(),
          onError: () => resolve(),
        })
      })

      const saveTranscriptsPromise = isSpeechToTextEnabled
        ? saveTranscripts(room.roomSid).catch(() => {})
        : Promise.resolve()

      await Promise.all([endVideoCallPromise, saveTranscriptsPromise]).finally(() => window.close())
    }
  }

  useEffect(() => {
    if (callMutation.current || room) {
      return
    }

    if (appointmentId) {
      const createVideoCallInput: StartVideoCallInput = {
        id: appointmentId,
      }
      startVideoCall(createVideoCallInput)
    } else {
      dummyStartVideoCall()
    }

    callMutation.current = true
  }, [room, appointmentId, dummyStartVideoCall, startVideoCall])

  // Handle patient voice toggle - mute/unmute audio and start/stop speech-to-text
  useEffect(() => {
    // Only act when isPatientVoiceEnabled actually changes, not when speechToTextRemote reference changes
    if (prevIsPatientVoiceEnabledRef.current === isPatientVoiceEnabled) {
      return
    }

    const audioTrack = remoteAudioTrackRef.current

    // Always stop first to clean up any existing session and prevent duplicates
    speechToTextRemote.stopRecording()

    if (isPatientVoiceEnabled) {
      // Enable audio playback (unmute)
      if (audioTrack) {
        audioTrack.mediaStreamTrack.enabled = true
        console.log("[DummyVideoCall] Patient voice enabled - audio unmuted")
      }
      // Start speech-to-text if feature enabled and track exists
      if (isSpeechToTextEnabled && audioTrack) {
        // Clone the track so stopRecording() won't kill the original Twilio track
        // (stopRecording calls track.stop() which permanently ends the track)
        const clonedTrack = audioTrack.mediaStreamTrack.clone()
        const freshStream = new MediaStream([clonedTrack])
        speechToTextRemote.startRecordingWithStream(freshStream).catch((error) => {
          console.error("Failed to start remote speech-to-text:", error)
        })
      }
    } else {
      // Disable audio playback (mute)
      if (audioTrack) {
        audioTrack.mediaStreamTrack.enabled = false
        console.log("[DummyVideoCall] Patient voice disabled - audio muted")
      }
    }

    prevIsPatientVoiceEnabledRef.current = isPatientVoiceEnabled
  }, [isPatientVoiceEnabled, isSpeechToTextEnabled, speechToTextRemote])

  return (
    <div className="relative h-screen w-screen">
      {/* Main video call area */}
      <div className="h-full w-full overflow-hidden">
        {isLoading ? (
          <LoadingSpinner isFullPage={true} size={24} />
        ) : (
          <VideoCall
            handleCloseVideoCall={handleCloseVideoCall}
            isDisconnected={isDisconnected}
            isSpeechToTextEnabled={isSpeechToTextEnabled}
            showTranscript={showTranscript}
            onToggleTranscript={() => setShowTranscript(!showTranscript)}
            onMicrophoneToggle={handleMicrophoneToggle}
          />
        )}
      </div>

      {/* Transcript panel - absolute positioned in right bottom corner */}
      {isSpeechToTextEnabled && showTranscript && !isLoading && (
        <div className="bg-surface border-border absolute right-4 bottom-4 z-50 flex h-80 w-96 flex-col overflow-hidden rounded-lg border shadow-lg">
          <div className="border-border bg-background-subdued flex shrink-0 items-center justify-end border-b px-4 py-2">
            <Switch
              id="patient-voice-toggle"
              checked={isPatientVoiceEnabled}
              onCheckedChange={() => setIsPatientVoiceEnabled(!isPatientVoiceEnabled)}
              label={t("speechToText.patientVoiceToggle")}
            />
          </div>
          <TranscriptDisplay
            transcripts={transcriptStore.getMergedTranscripts()}
            isRecording={transcriptStore.agentIsRecording || transcriptStore.patientIsRecording}
            className="min-h-0 flex-1"
          />
        </div>
      )}

      {SofiaEnvironment.AllowDebugFeatures && (
        <DummyRoomInformation roomSid={room?.roomSid ?? ""} patientToken={room?.dummyToken ?? ""} />
      )}
    </div>
  )
}

const DummyVideoCallLayout = () => {
  return (
    <Suspense fallback={<LoadingSpinner isFullPage={true} size={24} />}>
      <DummyVideoCallLayoutContent />
    </Suspense>
  )
}

export { DummyVideoCallLayout }
